/**
 * Security Utility Functions
 */

/**
 * Validates if a URL is safe for redirection.
 * Prevents Open Redirect vulnerabilities by checking if the URL is:
 * 1. A relative path (starts with / but not //)
 * 2. An absolute URL matching the current origin or a whitelist
 * 3. Not a javascript: scheme
 *
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if the URL is safe, false otherwise
 */
export const isValidRedirect = (url) => {
    if (!url) return false;

    // 1. Block dangerous schemes immediately
    // Note: Using a simple regex to catch common javascript: bypasses
    // decodeURIComponent helps catch encoded attempts like j%61vacript:
    try {
        const decodedUrl = decodeURIComponent(url).toLowerCase();
        // eslint-disable-next-line no-script-url
        if (decodedUrl.trim().startsWith('javascript:')) {
            return false;
        }
    } catch (e) {
        // malformed URI is automatically unsafe
        return false;
    }

    // 2. Allow relative paths (e.g. /dashboard)
    // Must start with / and NOT // (protocol relative) or /\ (windows path hacks)
    if (url.startsWith('/') && !url.startsWith('//') && !url.startsWith('/\\')) {
        return true;
    }

    // 3. Allow absolute URLs matching the current origin
    try {
        const urlObj = new URL(url);
        const currentOrigin = window.location.origin;

        // Check if origin matches
        if (urlObj.origin === currentOrigin) {
            return true;
        }

        // OPTIONAL: Add whitelist of allow external domains here if needed
        // const allowedDomains = ['https://trusted.sso.com'];
        // if (allowedDomains.includes(urlObj.origin)) return true;

    } catch (e) {
        // invalid URL string
        return false;
    }

    return false;
};

/**
 * Sanitize a redirect URL.
 * Returns the URL if valid, otherwise returns the fallback (defaulting to root /).
 * 
 * @param {string} url - Candidate URL
 * @param {string} fallback - Fallback URL (default '/')
 * @returns {string} - Safe URL
 */
export const getSafeRedirect = (url, fallback = '/') => {
    return isValidRedirect(url) ? url : fallback;
};
